import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';

const initialState = {
  notesByCandidateId: {},
  lastUpdated: {},
  candidateDbIds: {}
};

const actions = {
  SYNC_NOTES: 'SYNC_NOTES',
  ADD_NOTE: 'ADD_NOTE',
  UPDATE_NOTE: 'UPDATE_NOTE',
  DELETE_NOTE: 'DELETE_NOTE',
  CLEAR_NOTES: 'CLEAR_NOTES',
  SYNC_CANDIDATE_DB_ID: 'SYNC_CANDIDATE_DB_ID'
};

const notesReducer = (state, action) => {
  switch (action.type) {
    case actions.SYNC_NOTES: {
      const existingNotes = state.notesByCandidateId[action.candidateId] || [];
      const newNotes = action.notes || [];

      if (existingNotes.length !== newNotes.length) {
        return {
          ...state,
          notesByCandidateId: {
            ...state.notesByCandidateId,
            [action.candidateId]: action.notes
          },
          lastUpdated: {
            ...state.lastUpdated,
            [action.candidateId]: Date.now()
          }
        };
      }

      const notesAreDifferent = JSON.stringify(existingNotes) !== JSON.stringify(newNotes);

      if (notesAreDifferent) {
        return {
          ...state,
          notesByCandidateId: {
            ...state.notesByCandidateId,
            [action.candidateId]: action.notes
          },
          lastUpdated: {
            ...state.lastUpdated,
            [action.candidateId]: Date.now()
          }
        };
      }

      return state;
    }

    case actions.ADD_NOTE: {
      const existingNotes = state.notesByCandidateId[action.candidateId] || [];

      if (action.note._id && existingNotes.some(note => note._id === action.note._id)) {
        return state;
      }

      return {
        ...state,
        notesByCandidateId: {
          ...state.notesByCandidateId,
          [action.candidateId]: [
            ...existingNotes,
            action.note
          ]
        },
        lastUpdated: {
          ...state.lastUpdated,
          [action.candidateId]: Date.now()
        }
      };
    }

    case actions.UPDATE_NOTE: {
      const existingNotes = state.notesByCandidateId[action.candidateId] || [];
      const noteExists = existingNotes.some(note => note._id === action.noteId);

      if (!noteExists) {
        return state;
      }

      return {
        ...state,
        notesByCandidateId: {
          ...state.notesByCandidateId,
          [action.candidateId]: existingNotes.map(note =>
            note._id === action.noteId ? { ...note, ...action.updates } : note
          )
        },
        lastUpdated: {
          ...state.lastUpdated,
          [action.candidateId]: Date.now()
        }
      };
    }

    case actions.DELETE_NOTE: {
      const existingNotes = state.notesByCandidateId[action.candidateId] || [];
      const noteExists = existingNotes.some(note => note._id === action.noteId);

      if (!noteExists) {
        return state;
      }

      return {
        ...state,
        notesByCandidateId: {
          ...state.notesByCandidateId,
          [action.candidateId]: existingNotes.filter(note => note._id !== action.noteId)
        },
        lastUpdated: {
          ...state.lastUpdated,
          [action.candidateId]: Date.now()
        }
      };
    }

    case actions.CLEAR_NOTES:
      return initialState;

    case actions.SYNC_CANDIDATE_DB_ID: {
      if (!action.sid || !action.candidateDbId) return state;

      if (state.candidateDbIds[action.sid] === action.candidateDbId) {
        return state;
      }

      return {
        ...state,
        candidateDbIds: {
          ...state.candidateDbIds,
          [action.sid]: action.candidateDbId
        }
      };
    }

    default:
      return state;
  }
};

const NotesContext = createContext();

export const NotesProvider = ({ children }) => {
  const [state, dispatch] = useReducer(notesReducer, initialState);
  const syncTimeoutRef = useRef({});

  useEffect(() => {
    return () => {
      Object.values(syncTimeoutRef.current).forEach(timeoutId => {
        clearTimeout(timeoutId);
      });
    };
  }, []);

  const syncNotes = useCallback((candidateId, notes) => {
    if (!candidateId) return;

    const notesArray = Array.isArray(notes) ? notes :
      notes && Array.isArray(notes.notes) ? notes.notes :
        notes ? [notes] : [];

    if (notesArray.length === 0) {
      return;
    }



    if (syncTimeoutRef.current[candidateId]) {
      clearTimeout(syncTimeoutRef.current[candidateId]);
    }

    syncTimeoutRef.current[candidateId] = setTimeout(() => {


      const existingNotes = state.notesByCandidateId[candidateId] || [];

      if (existingNotes.length > 1 && notesArray.length === 1) {

        const newNote = notesArray[0];
        const noteExists = newNote._id && existingNotes.some(note => note._id === newNote._id);

        if (!noteExists) {
          const mergedNotes = [...existingNotes, newNote];

          dispatch({
            type: actions.SYNC_NOTES,
            candidateId,
            notes: mergedNotes
          });

        }
        // Clear the timeout reference and exit
        delete syncTimeoutRef.current[candidateId];
        return;
      }


      const lengthDiffers = existingNotes.length !== notesArray.length;

      let contentDiffers = false;
      if (!lengthDiffers) {
        const sortById = (a, b) => (a._id || '').localeCompare(b._id || '');
        const sortedExisting = [...existingNotes].sort(sortById);
        const sortedNew = [...notesArray].sort(sortById);


        contentDiffers = sortedExisting.some((note, index) =>
          !sortedNew[index] || note._id !== sortedNew[index]._id);

        if (!contentDiffers) {

          const stringifiedExisting = JSON.stringify(sortedExisting);
          const stringifiedNew = JSON.stringify(sortedNew);
          contentDiffers = stringifiedExisting !== stringifiedNew;
        }
      }

      if (lengthDiffers || contentDiffers) {


        dispatch({
          type: actions.SYNC_NOTES,
          candidateId,
          notes: notesArray
        });
      }


      delete syncTimeoutRef.current[candidateId];
    }, 150);
  }, [state.notesByCandidateId, dispatch]);

  const addNote = useCallback((candidateId, note) => {
    if (!candidateId) return;

    if (!note) return;

    const existingNotes = state.notesByCandidateId[candidateId] || [];

    if (Array.isArray(note)) {

      const notesToSync = [...existingNotes];

      note.forEach(newNote => {
        if (!newNote._id || !existingNotes.some(n => n._id === newNote._id)) {
          notesToSync.push(newNote);
        }
      });

      return syncNotes(candidateId, notesToSync);
    }

    if (note._id && existingNotes.some(n => n._id === note._id)) {
      return;
    }



    const updatedNotes = [...existingNotes, note];


    syncNotes(candidateId, updatedNotes);
  }, [state.notesByCandidateId, syncNotes]);


  const updateNote = useCallback((candidateId, noteId, updates) => {
    if (!candidateId || !noteId || !updates) return;

    dispatch({
      type: actions.UPDATE_NOTE,
      candidateId,
      noteId,
      updates
    });
  }, []);


  const deleteNote = useCallback((candidateId, noteId) => {
    if (!candidateId || !noteId) return;

    dispatch({
      type: actions.DELETE_NOTE,
      candidateId,
      noteId
    });
  }, []);

  const clearAllNotes = useCallback(() => {
    dispatch({ type: actions.CLEAR_NOTES });
  }, []);

  const getCandidateNotes = useCallback((candidateId) => {
    return state.notesByCandidateId[candidateId] || [];
  }, [state.notesByCandidateId]);


  const getLastUpdated = useCallback((candidateId) => {
    return state.lastUpdated[candidateId] || 0;
  }, [state.lastUpdated]);
  const syncCandidateDbId = useCallback((sid, candidateDbId) => {
    if (!sid || !candidateDbId) return;

    dispatch({
      type: actions.SYNC_CANDIDATE_DB_ID,
      sid,
      candidateDbId
    });
  }, []);

  const getCandidateDbId = useCallback((sid) => {
    return state.candidateDbIds[sid] || null;
  }, [state.candidateDbIds]);



  const contextValue = {
    syncNotes,
    addNote,
    updateNote,
    deleteNote,
    clearAllNotes,
    getCandidateNotes,
    getLastUpdated,
    syncCandidateDbId,
    getCandidateDbId
  };

  return (
    <NotesContext.Provider value={contextValue}>
      {children}
    </NotesContext.Provider>
  );
};


export const useNotes = () => {
  const context = useContext(NotesContext);
  if (!context) {
    throw new Error('useNotes must be used within a NotesProvider');
  }
  return context;
};
